import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const EditServiceLayer = () => {
  const { id } = useParams();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [originalCategoryId, setOriginalCategoryId] = useState("");
  const [originalSubcategoryId, setOriginalSubcategoryId] = useState("");
  // Only send subcategory_id when the user actually changed it.
  // Omitting the field keeps the current subcategory on the backend.
  const [subcategoryTouched, setSubcategoryTouched] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/service-categories?limit=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await response.json();
        if (response.ok) {
          setCategories(json.data.categories || []);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [token]);

  // Subcategories of the selected category only
  useEffect(() => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    let cancelled = false;
    const fetchSubcategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/service-subcategories?category_id=${categoryId}&limit=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await response.json();
        if (!cancelled && response.ok) {
          setSubcategories(json.data.subcategories || []);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
      }
    };
    fetchSubcategories();
    return () => {
      cancelled = true;
    };
  }, [categoryId, token]);

  useEffect(() => {
    const fetchService = async () => {
      const loading = toast.loading("Loading service data...");
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}api/admin/services?limit=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await response.json();
        if (response.ok) {
          const service = (json.data.services || []).find(
            (s) => String(s.id) === String(id)
          );
          if (service) {
            const catId = service.category?.id?.toString() || "";
            const subId = service.subcategory?.id?.toString() || "";
            setCategoryId(catId);
            setSubcategoryId(subId);
            setOriginalCategoryId(catId);
            setOriginalSubcategoryId(subId);
            setSubcategoryTouched(false);
            setName(service.name || "");
            setDescription(service.description || "");
            setDurationMinutes(service.durationMinutes?.toString() || "");
          } else {
            toast.error("Service not found");
            navigate("/service-catalog");
          }
        }
        toast.dismiss(loading);
      } catch (error) {
        console.error(error);
        toast.dismiss(loading);
        toast.error("An error occurred while loading service data");
        navigate("/service-catalog");
      }
    };

    fetchService();
  }, [id, token, navigate]);

  const handleCategoryChange = (value) => {
    setCategoryId(value);
    if (value === originalCategoryId) {
      // back to the original category: restore the original subcategory
      setSubcategoryId(originalSubcategoryId);
      setSubcategoryTouched(false);
    } else {
      // a subcategory belongs to one category, so reset it
      setSubcategoryId("");
      setSubcategoryTouched(true);
    }
  };

  const handleSubcategoryChange = (value) => {
    setSubcategoryId(value);
    setSubcategoryTouched(true);
  };

  const validateForm = () => {
    let errors = {};
    if (!categoryId) errors.categoryId = "Category is required";
    if (!name.trim()) errors.name = "Service name is required";

    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((error) => toast.error(error));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append("category_id", categoryId);
    if (subcategoryTouched) {
      // empty value takes the service out of its subcategory
      formData.append("subcategory_id", subcategoryId);
    }
    formData.append("name", name.trim());
    formData.append("description", description.trim());
    if (durationMinutes) {
      formData.append("duration_minutes", durationMinutes);
    }

    const loading = toast.loading("Updating service...");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/admin/services/${id}`,
        {
          method: "PUT",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const json = await response.json();
      if (response.ok) {
        toast.dismiss(loading);
        toast.success("Service updated successfully");
        navigate("/service-catalog");
      } else {
        toast.dismiss(loading);
        // backend validation messages are shown as-is
        toast.error(json.errorMessage || "Failed to update service");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while updating the service");
    } finally {
      toast.dismiss(loading);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-40">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Category <span className="text-danger-600">*</span>
                </label>
                <select
                  className="form-select radius-8"
                  value={categoryId}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Subcategory
                </label>
                <select
                  className="form-select radius-8"
                  value={subcategoryId}
                  onChange={(e) => handleSubcategoryChange(e.target.value)}
                  disabled={!categoryId}
                >
                  <option value="">
                    {categoryId ? "No subcategory" : "Select a category first"}
                  </option>
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-12">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Name <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="col-sm-6">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  min="1"
                  className="form-control radius-8"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                />
              </div>
            </div>

            <div className="col-12">
              <div className="mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Description
                </label>
                <textarea
                  className="form-control radius-8"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
              <button
                type="button"
                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                onClick={() => navigate("/service-catalog")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
              >
                Update
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditServiceLayer;